import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { billingApi } from '../../services/api/billing';
import { TokenBalance, TokenUsage, PaymentMethod } from '../../types/billing';
import { TokenPurchase } from '../../components/Billing/TokenPurchase';
import TokenUsageDashboard from '../../components/TokenUsage/TokenUsageDashboard';
import { useAuth } from '../../hooks/useAuth';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`token-tabpanel-${index}`}
      aria-labelledby={`token-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ paddingTop: 24 }}>{children}</div>}
    </div>
  );
};

export const TokenUsagePage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [tokenBalance, setTokenBalance] = useState<TokenBalance | null>(null);
  const [tokenUsage, setTokenUsage] = useState<TokenUsage | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  const fetchTokenData = useCallback(async () => {
    if (!user?.organizationId) return;

    setLoading(true);
    setError(null);

    try {
      const [balance, usage, methods] = await Promise.all([
        billingApi.getTokenBalance(user.organizationId),
        billingApi.getTokenUsage(user.organizationId),
        billingApi.getPaymentMethods(user.organizationId),
      ]);

      setTokenBalance(balance);
      setTokenUsage(usage);
      setPaymentMethods(methods);
    } catch (err) {
      console.error('Error fetching token data:', err);
      setError('Failed to load token information. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId]);

  useEffect(() => {
    fetchTokenData();
  }, [fetchTokenData]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Container maxWidth="xl" style={{ paddingTop: 32, paddingBottom: 32 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Token Usage & Purchase
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: 24 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper style={{ marginBottom: 24 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Usage Dashboard" />
          <Tab label="Purchase Tokens" />
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <TokenUsageDashboard />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {tokenBalance && tokenUsage && user?.organizationId && (
          <TokenPurchase
            organizationId={user.organizationId}
            tokenBalance={tokenBalance}
            tokenUsage={tokenUsage}
            paymentMethods={paymentMethods}
            onPurchaseComplete={fetchTokenData}
          />
        )}
      </TabPanel>
    </Container>
  );
};